import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { Box, Stack, Typography, Alert } from "@mui/material";
import { ImportTableViewModel, JobsImportTableProps, MergeConflict, MergeResolutions } from "./ImportTable.types";
import { JobCard } from "./JobCard";
import { ImportTableToolbarMobile } from "./ImportTable.toolbar-mobile";
import { ValidationErrorsContext } from "./ValidationErrorsContext";
import { MergeConflictModal } from "./MergeConflictModal";

/**
 * Props for the mobile ImportTable component
 */
export interface ImportTableMobileProps {
	viewModel: ImportTableViewModel;
	props: JobsImportTableProps;
	/** Map visibility controls (passed through for map feature) */
	mapDisplayMode?: "modal" | "drawer" | null;
	setMapDisplayMode?: (mode: "modal" | "drawer" | null) => void;
	/** For split feature - IDs of features selected via lasso on map */
	selectedFeatureIds?: Set<string>;
	/** Callback to clear feature selection after split */
	onClearFeatureSelection?: () => void;
}

/**
 * Mobile view of ImportTable using card-based layout.
 * Designed for phone screens (<600px viewport).
 *
 * Features:
 * - Expandable job cards instead of table rows
 * - Touch-friendly 48px minimum targets
 * - Sticky toolbar with FAB for adding jobs
 * - Same validation behavior as desktop
 * - Multi-select for bulk operations
 */
export const ImportTableMobile: React.FC<ImportTableMobileProps> = ({
	viewModel,
	props,
	mapDisplayMode,
	setMapDisplayMode,
	selectedFeatureIds,
	onClearFeatureSelection,
}) => {
	const { jobs, validationErrors, columnContext } = viewModel;
	const { setDisplayedJobs, salespersons, branches, allDeals } = props;

	// Track which cards are expanded
	const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());

	// Track selected cards for bulk operations
	const [selectedCards, setSelectedCards] = useState<Set<string>>(new Set());

	// Merge conflict modal state
	const [mergeConflictModalOpen, setMergeConflictModalOpen] = useState(false);
	const [pendingMergeIndices, setPendingMergeIndices] = useState<number[]>([]);
	const [mergeConflicts, setMergeConflicts] = useState<MergeConflict[]>([]);

	// Track previous selection to prevent infinite loops
	const prevSelectedRef = useRef<string>("");

	// Sync selected cards with map - convert job IDs to index-based selection for viewModel
	useEffect(() => {
		// Serialize current selection for comparison
		const currentSelection = Array.from(selectedCards).sort().join(",");

		// Only update if selection actually changed
		if (currentSelection === prevSelectedRef.current) {
			return;
		}
		prevSelectedRef.current = currentSelection;

		if (setDisplayedJobs) {
			if (selectedCards.size > 0) {
				// Convert job IDs to MRT-style row selection (index-based)
				const rowSelection: Record<string, boolean> = {};
				jobs.forEach((job, index) => {
					if (selectedCards.has(job.id)) {
						rowSelection[index.toString()] = true;
					}
				});
				viewModel.updateDisplayedJobs(rowSelection);
			} else {
				// Clear map data when no cards selected
				setDisplayedJobs([]);
			}
		}
	}, [selectedCards, jobs, setDisplayedJobs, viewModel]);

	// Toggle card expansion
	const handleToggle = useCallback((jobId: string) => {
		setExpandedCards((prev) => {
			const next = new Set(prev);
			if (next.has(jobId)) {
				next.delete(jobId);
			} else {
				next.add(jobId);
			}
			return next;
		});
	}, []);

	// Toggle card selection
	const handleSelectToggle = useCallback((jobId: string) => {
		setSelectedCards((prev) => {
			const next = new Set(prev);
			if (next.has(jobId)) {
				next.delete(jobId);
			} else {
				next.add(jobId);
			}
			return next;
		});
	}, []);

	// Handle job deletion
	const handleDelete = useCallback(
		(jobId: string) => {
			const index = jobs.findIndex((j) => j.id === jobId);
			if (index !== -1) {
				viewModel.deleteJobs([index]);
				setSelectedCards((prev) => {
					const next = new Set(prev);
					next.delete(jobId);
					return next;
				});
			}
		},
		[jobs, viewModel],
	);

	// Handle job duplication
	const handleDuplicate = useCallback(
		(jobId: string) => {
			const index = jobs.findIndex((j) => j.id === jobId);
			if (index !== -1) {
				viewModel.duplicateJobs([index]);
			}
		},
		[jobs, viewModel],
	);

	// Bulk delete selected jobs
	const handleBulkDelete = useCallback(() => {
		const indices = jobs.map((job, index) => (selectedCards.has(job.id) ? index : -1)).filter((index) => index !== -1);
		if (indices.length > 0) {
			const count = indices.length;
			if (!window.confirm(`Are you sure you want to delete ${count} job${count === 1 ? "" : "s"}?`)) {
				return;
			}
			viewModel.deleteJobs(indices);
			setSelectedCards(new Set());
		}
	}, [jobs, selectedCards, viewModel]);

	// Bulk duplicate selected jobs
	const handleBulkDuplicate = useCallback(() => {
		const indices = jobs.map((job, index) => (selectedCards.has(job.id) ? index : -1)).filter((index) => index !== -1);
		if (indices.length > 0) {
			viewModel.duplicateJobs(indices);
			setSelectedCards(new Set());
		}
	}, [jobs, selectedCards, viewModel]);

	// Bulk merge selected jobs
	const handleBulkMerge = useCallback(() => {
		const indices = jobs.map((job, index) => (selectedCards.has(job.id) ? index : -1)).filter((index) => index !== -1);
		if (indices.length >= 2) {
			const count = indices.length;
			if (!window.confirm(`Are you sure you want to merge ${count} jobs?`)) {
				return;
			}

			// Detect conflicts before merging
			const conflicts = viewModel.detectMergeConflicts(indices);

			if (conflicts.length > 0) {
				// Show modal to resolve conflicts
				setPendingMergeIndices(indices);
				setMergeConflicts(conflicts);
				setMergeConflictModalOpen(true);
			} else {
				// No conflicts, proceed with merge
				viewModel.mergeJobs(indices);
				setSelectedCards(new Set());
			}
		}
	}, [jobs, selectedCards, viewModel]);

	// Handle merge conflict resolution
	const handleMergeConflictConfirm = useCallback(
		(resolutions: MergeResolutions) => {
			viewModel.mergeJobs(pendingMergeIndices, resolutions);
			setSelectedCards(new Set());
			setMergeConflictModalOpen(false);
			setPendingMergeIndices([]);
			setMergeConflicts([]);
		},
		[viewModel, pendingMergeIndices],
	);

	const handleMergeConflictCancel = useCallback(() => {
		setMergeConflictModalOpen(false);
		setPendingMergeIndices([]);
		setMergeConflicts([]);
	}, []);

	// Split selected job (requires feature selection via map lasso)
	const handleSplitJob = useCallback(() => {
		if (selectedCards.size !== 1) return;
		const selectedJobId = Array.from(selectedCards)[0];
		const jobIndex = jobs.findIndex((j) => j.id === selectedJobId);
		if (jobIndex === -1) return;
		if (!selectedFeatureIds?.size) return;

		viewModel.splitJob(jobIndex, selectedFeatureIds, onClearFeatureSelection);
		setSelectedCards(new Set());
	}, [jobs, selectedCards, viewModel, selectedFeatureIds, onClearFeatureSelection]);

	// Get the ID of the single selected job (for split feature validation)
	const getSelectedJobId = (): string | undefined => {
		if (selectedCards.size !== 1) return undefined;
		return Array.from(selectedCards)[0];
	};

	// Select/deselect all
	const handleSelectAll = useCallback(() => {
		if (selectedCards.size === jobs.length) {
			setSelectedCards(new Set());
		} else {
			setSelectedCards(new Set(jobs.map((j) => j.id)));
		}
	}, [jobs, selectedCards]);

	// Validation error summary
	const errorSummary = useMemo(() => {
		return viewModel.getValidationErrorSummary();
	}, [viewModel]);

	const hasErrors = viewModel.hasValidationErrors();

	return (
		<ValidationErrorsContext.Provider value={validationErrors}>
			<Box
				data-testid="import-table-mobile"
				sx={{
					display: "flex",
					flexDirection: "column",
					height: "100%",
					position: "relative",
				}}
			>
				{/* Mobile Toolbar */}
				<ImportTableToolbarMobile
					jobCount={jobs.length}
					selectedCount={selectedCards.size}
					hasValidationErrors={hasErrors}
					onAddJob={viewModel.addBlankJob}
					onDeleteSelected={handleBulkDelete}
					onDuplicateSelected={handleBulkDuplicate}
					onMergeSelected={handleBulkMerge}
					onSplitSelected={handleSplitJob}
					onSelectAll={handleSelectAll}
					onCreateJobs={viewModel.createJobs}
					onExportCSV={viewModel.exportCSV}
					mapDisplayMode={mapDisplayMode}
					setMapDisplayMode={setMapDisplayMode}
					selectedFeatureIds={selectedFeatureIds}
					selectedJobId={getSelectedJobId()}
				/>

				{/* Job Cards List */}
				<Box
					sx={{
						flex: 1,
						overflow: "auto",
						p: 2,
						pb: 10, // Extra padding for FAB
					}}
				>
					{/* Validation Error Summary - scrolls with cards */}
					{hasErrors && (errorSummary.missing.length > 0 || errorSummary.invalid.length > 0) && (
						<Alert severity="error" sx={{ mb: 2 }}>
							<Typography variant="body2" fontWeight="bold">
								Please fix validation errors before submitting:
							</Typography>
							{errorSummary.missing.length > 0 && (
								<>
									<Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
										Missing:
									</Typography>
									<ul style={{ margin: "4px 0 0 0", paddingLeft: "20px" }}>
										{errorSummary.missing.slice(0, 3).map((err) => (
											<li key={err.field}>
												{err.field}: {err.count} {err.count === 1 ? "field" : "fields"}
											</li>
										))}
										{errorSummary.missing.length > 3 && (
											<li>...and {errorSummary.missing.length - 3} more missing fields</li>
										)}
									</ul>
								</>
							)}
							{errorSummary.invalid.length > 0 && (
								<>
									<Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
										Invalid:
									</Typography>
									<ul style={{ margin: "4px 0 0 0", paddingLeft: "20px" }}>
										{errorSummary.invalid.slice(0, 3).map((err) => (
											<li key={err.field}>
												{err.field}: {err.count} {err.count === 1 ? "error" : "errors"}
											</li>
										))}
										{errorSummary.invalid.length > 3 && (
											<li>...and {errorSummary.invalid.length - 3} more invalid fields</li>
										)}
									</ul>
								</>
							)}
						</Alert>
					)}

					{jobs.length === 0 ? (
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								justifyContent: "center",
								minHeight: 200,
								color: "text.secondary",
							}}
						>
							<Typography variant="h6">No jobs to display</Typography>
							<Typography variant="body2">Tap + to add a new job</Typography>
						</Box>
					) : (
						<Stack spacing={0}>
							{jobs.map((job) => (
								<JobCard
									key={job.id}
									job={job}
									validationErrors={validationErrors}
									columnContext={columnContext}
									expanded={expandedCards.has(job.id)}
									onToggle={handleToggle}
									onDelete={handleDelete}
									onDuplicate={handleDuplicate}
									selected={selectedCards.has(job.id)}
									onSelectToggle={handleSelectToggle}
								/>
							))}
						</Stack>
					)}
				</Box>

				{/* Merge Conflict Modal */}
				<MergeConflictModal
					open={mergeConflictModalOpen}
					conflicts={mergeConflicts}
					onConfirm={handleMergeConflictConfirm}
					onCancel={handleMergeConflictCancel}
					salespersons={salespersons}
					branches={branches}
					deals={allDeals}
				/>
			</Box>
		</ValidationErrorsContext.Provider>
	);
};
